import { useEffect, useMemo, useState } from "react";

import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import { LoomPatternHierarchyView } from "./LoomPatternHierarchy";
import {
  createLoomHighLiftSectionRequest,
  createLoomPatternHierarchy,
  type LoomHighLiftSectionRequest,
  type LoomPatternGraphProjection,
  type LoomPatternSectionProjection,
} from "./loomPatternPlaybook";

interface LoomPatternPlaybookPanelProps {
  document: LoomAuthoringDocument;
}

function collectSections(node: LoomPatternGraphProjection, sections: LoomPatternSectionProjection[]): void {
  node.wing?.panels.forEach((panel) => sections.push(...panel.sections));
  node.inputs.forEach((input) => collectSections(input, sections));
}

export function LoomPatternPlaybookPanel({ document }: LoomPatternPlaybookPanelProps) {
  const hierarchy = useMemo(() => createLoomPatternHierarchy(document), [document]);
  const sections = useMemo(() => {
    const result: LoomPatternSectionProjection[] = [];
    hierarchy.outputs.forEach((output) => collectSections(output.graph, result));
    return [...new Map(result.map((section) => [section.id, section])).values()];
  }, [hierarchy]);
  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const selectedSection = sections.find((section) => section.id === selectedSectionId);
  useEffect(() => {
    if (selectedSectionId && !selectedSection) setSelectedSectionId(undefined);
  }, [selectedSection, selectedSectionId]);
  const handoff = useMemo<{ request: LoomHighLiftSectionRequest } | { error: string } | null>(() => {
    if (!selectedSection) return null;
    try {
      return { request: createLoomHighLiftSectionRequest({
        document,
        nodeId: selectedSection.nodeId,
        panelId: selectedSection.panelId,
        side: selectedSection.side,
        requestId: `high-lift:${selectedSection.id}`,
      }) };
    } catch (error) {
      return { error: error instanceof Error ? error.message : "This section cannot be handed to High-Lift." };
    }
  }, [document, selectedSection]);
  const request = handoff && "request" in handoff ? handoff.request : null;

  return (
    <details className="loom-pattern-playbook" data-testid="loom-pattern-playbook">
      <summary>
        <span>Pattern playbook</span>
        <em>aircraft → wing → section</em>
      </summary>
      <div className="loom-pattern-playbook-body">
        <p>A Pattern is this recipe&apos;s graph clique. Derived surfaces stay linked to their authored source; section work is delegated with Loom-owned limits.</p>
        <LoomPatternHierarchyView
          hierarchy={hierarchy}
          onSelectSection={(section) => setSelectedSectionId(section.id)}
          selectedSectionId={selectedSectionId}
        />
        {request ? (
          <section
            data-document-state-id={request.source.stateId}
            data-node-id={request.source.nodeId}
            data-panel-id={request.source.panelId}
            data-request-id={request.requestId}
            data-section-id={request.source.sectionId}
            data-section-side={request.source.side}
            data-testid="loom-pattern-section-handoff"
          >
            <header><span>Selected section</span><strong>{request.source.side} · {request.sectionDefinition.family}</strong></header>
            <div><strong>High-Lift child editor</strong><small>typed handoff ready</small></div>
            <dl>
              <div><dt>Airfoils</dt><dd>{request.limits.allowedFamilies.join(" · ")}</dd></div>
              <div><dt>Devices</dt><dd>{request.limits.highLift.allowedDeviceKinds.join(" · ")}</dd></div>
              <div><dt>LE morph</dt><dd>{request.limits.highLift.allowDroopNose ? "droop nose allowed" : "not allowed"}</dd></div>
              <div><dt>Deflection</dt><dd>{request.limits.highLift.deflectionDeg.min}°…{request.limits.highLift.deflectionDeg.max}°</dd></div>
            </dl>
            <small>Loom owns this Pattern head and its control limits. High-Lift must return a content-addressed section artifact before one immutable Pattern operation can apply it. Editor navigation is not connected in this build.</small>
          </section>
        ) : handoff && "error" in handoff ? (
          <section className="is-blocked" data-testid="loom-pattern-section-handoff-blocked" role="status">
            <header><span>Section handoff blocked</span><strong>High-Lift limits</strong></header>
            <small>{handoff.error}</small>
          </section>
        ) : <small>Select a root or tip section to prepare its bounded High-Lift handoff.</small>}
      </div>
    </details>
  );
}
